import React, { useState } from "react";
import styled from "@emotion/styled";
import { FolderTitleRow } from "./FolderTitleRow";
import cn from "classnames";
import _ from "lodash";
import { AnyFolder } from "./types";
import { getFolderName } from "./utils";

export const route = "project-folders";

const FolderDiv = styled("div")`
  padding-left: 8px;
  padding-right: 8px;
  position: relative;

  .children {
    margin-left: 16px;
    height: 0;
    overflow: hidden;
    transition: max-height 0.4s ease;

    &.expanded {
      height: auto;
    }
  }
`;

type FolderProps = {
  folder: AnyFolder;
  selectable: boolean;
  onSelect: React.Dispatch<AnyFolder>;
  selectedFolder: AnyFolder | null;
};

function getSubfolders(folder: AnyFolder): AnyFolder[] | null {
  switch (folder.__typename) {
    case "StructuralFolder":
      return folder.clientFolders;
    case "CustomerFolder":
      return [...folder.projectFolders, ...folder.groupedFolders];
    case "GroupedFolder":
      return null;
    case "ProjectFolder":
      return null;
  }

  // should be unreachable
  throw new Error("unexpected folder type");
}

export const FolderDisplay: React.FC<FolderProps> = ({
  folder,
  selectable,
  onSelect,
  selectedFolder,
}) => {
  const [expanded, setExpanded] = useState(false);

  const name = getFolderName(folder);
  const type = folder.__typename;
  const subfolders = getSubfolders(folder);
  const subfolderLength = subfolders?.length || 0;

  const clickHandler = () => {
    setExpanded((x) => !x);
    onSelect(folder);
  };

  const syncStatus =
    folder.__typename !== "StructuralFolder" ? folder.syncStatus : null;

  return (
    <FolderDiv>
      <FolderTitleRow
        type={type!}
        childCount={subfolderLength}
        name={name}
        expanded={expanded}
        onClick={clickHandler}
        selectable={selectable}
        onSelect={() => onSelect(folder)}
        selected={folder === selectedFolder}
        syncStatus={syncStatus}
      />

      {subfolders !== null && (
        <div className={cn("children", { expanded })}>
          {subfolders.map((x) => (
            <FolderDisplay
              folder={x}
              key={getFolderName(x)}
              selectable={expanded}
              onSelect={onSelect}
              selectedFolder={selectedFolder}
            />
          ))}
        </div>
      )}
    </FolderDiv>
  );
};
